<template>
	<div
		class="audio-recorder"
		:class="{ 'at-limit': reachedLimit, closing: isClosing }"
	>
		<div
			class="limit-text"
			v-if="reachedLimit"
			v-t="'audio_message_time_limit_reached'"
		/>
		<div class="wrapper">
			<TrashCanIcon type="regular" class="trash" @click="onClickTrash" />
			<div class="waveform">
				<CirclePlayIcon
					v-if="hasStoppedRecording && !isPlaying"
					class="play"
					type="regular"
					@click="player.play()"
				/>
				<CirclePauseIcon
					v-if="hasStoppedRecording && isPlaying"
					class="pause"
					type="regular"
					@click="player.pause()"
				/>
				<div class="canvas-container" ref="container" @click="seekPlayback">
					<canvas ref="canvas" />
				</div>
				<div class="time" v-text="playbackTime" />
			</div>
			<PaperPlaneTopIcon
				v-if="hasStoppedRecording"
				class="send"
				type="solid"
				@click="onClickSend"
			/>
			<CircleStopIcon
				v-if="isRecording"
				class="stop"
				type="regular"
				@click="stopRecording"
			/>
		</div>
	</div>
</template>

<script>
import moment from 'moment'
import debounce from 'debounce'
import {
	CirclePlayIcon,
	CircleStopIcon,
	CirclePauseIcon,
	TrashCanIcon,
	PaperPlaneTopIcon,
} from '@/icons'

const FILE_NAME = 'audio_message.m4a'
const MIME_TYPE = 'audio/x-m4a'
const MIN_BAR_HEIGHT = 1
const BAR_WIDTH = 3
const BAR_SPACING = 3
const MAX_TIME_MS = 300000

export default {
	name: 'AudioRecorder',
	components: {
		CirclePlayIcon,
		CircleStopIcon,
		CirclePauseIcon,
		TrashCanIcon,
		PaperPlaneTopIcon,
	},
	props: { autoStart: Boolean },
	data() {
		return {
			isRecording: false,
			startTime: null,
			stopTime: null,
			audioBlobs: [],
			drawableBarCount: 0,
			fullBlob: null,
			liveAverages: [],
			player: null,
			isPlaying: false,
			currentPlaybackTime: 0,
			reachedLimit: false,
			isClosing: false,
		}
	},
	emits: ['cancel', 'send'],
	mounted() {
		this.resizeObserver = new ResizeObserver(debounce(this.onCanvasResize, 50))
		this.resizeObserver.observe(this.$refs.container)
		this.onCanvasResize()

		if (this.autoStart) {
			this.startRecording()
		}
	},
	beforeUnmount() {
		this.clearState()
	},
	computed: {
		recordTime() {
			if (!this.startTime || !this.stopTime) return 0
			return this.stopTime.diff(this.startTime)
		},
		recordTimeFormatted() {
			if (!this.recordTime) return '0:00'
			return moment.utc(this.recordTime).format('m:ss')
		},
		playbackTime() {
			if (!this.currentPlaybackTime) return this.recordTimeFormatted
			const ms = this.currentPlaybackTime * 1000
			return moment.utc(ms).format('m:ss')
		},
		hasStoppedRecording() {
			return !this.isRecording && this.liveAverages.length > 0
		},
		hasNotStartedRecording() {
			return !this.isRecording && !this.liveAverages.length
		},
	},
	watch: {
		isRecording(on) {
			if (on) {
				this.interval = setInterval(() => {
					this.stopTime = moment()
				}, 1000)
			} else {
				clearInterval(this.interval)
				this.interval = null
			}
		},

		isPlaying(isPlaying) {
			if (isPlaying) {
				this.interval = setInterval(() => this.drawPlayback(), 100)
			} else {
				clearInterval(this.interval)
				this.interval = null
			}
		},

		recordTime(ms) {
			if (ms >= MAX_TIME_MS) {
				this.stopRecording()
				this.reachedLimit = true
			}
		},

		drawableBarCount() {
			if (this.hasStoppedRecording) {
				this.requestFinalWaveformFromWorklet()
			}
		},
	},
	methods: {
		async getAccessToStream() {
			try {
				this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
				this.audioCtx = new AudioContext()
			} catch (e) {
				console.error(e)
				alert(e)
			}
		},

		onAudioData({ data }) {
			this.audioBlobs.push(data)
			if (!this.isRecording) {
				this.fullBlob = new Blob(this.audioBlobs, { type: MIME_TYPE })
				// we've stopped recording so tell the worklet to process all of the audio
				this.requestFinalWaveformFromWorklet()
			}
		},

		requestFinalWaveformFromWorklet() {
			this.workletNode.port.postMessage({
				event: 'calculateFinalWaveform',
				barCount: this.drawableBarCount,
			})
		},

		sendWorkletShutdown() {
			this.workletNode.port.postMessage({ event: 'shutdown' })
			setTimeout(() => {
				this.workletNode.disconnect()
			}, 100)
		},

		onWorkletMessage({ data }) {
			// leaving console log for future debugging help
			console.debug('audio recording worklet message', data)
			switch (data.event) {
				case 'average':
					return this.onWorkletAverage(data.average)
				case 'finalWaveform':
					return this.onWorkletFinalWaveform(data.averages)
			}
		},

		onWorkletAverage(average) {
			if (!this.isRecording) return
			this.liveAverages.push(average)
			const barsToDraw = this.liveAverages.slice(-this.drawableBarCount)
			this.drawWave(barsToDraw, () => ({ strokeStyle: '#49A4DA' }))
		},

		onWorkletFinalWaveform(averages) {
			this.fullWaveform = averages
			this.drawFullClip()
			this.setUpPlayback()
		},

		async startRecording() {
			if (!this.stream) {
				await this.getAccessToStream()
			}
			// set up MediaRecorder (see https://codepen.io/Sambego/pen/VmBJJN)
			this.streamSource = this.audioCtx.createMediaStreamSource(this.stream)
			this.analyser = this.audioCtx.createAnalyser()
			this.streamSource.connect(this.analyser)
			this.mediaRecorder = new MediaRecorder(this.stream)
			this.audioBlobs = []
			this.mediaRecorder.addEventListener('dataavailable', this.onAudioData)
			// connect the worklet module to process audio off the main thread
			await this.audioCtx.audioWorklet.addModule('/js/RecordAudioWorklet.js')
			this.workletNode = new AudioWorkletNode(this.audioCtx, 'recordAudio')
			this.workletNode.port.onmessage = this.onWorkletMessage
			this.streamSource
				.connect(this.workletNode)
				.connect(this.audioCtx.destination)
			// listen for track 'ended' event in case audio device disconnects
			const [track] = this.stream.getAudioTracks()
			track.addEventListener('ended', this.onAudioTrackEnded)
			// start recording
			this.isRecording = true
			this.startTime = moment()
			this.stopTime = moment()
			this.mediaRecorder.start()
			// start capturing live averages
			this.liveAverages = []
			this.drawableLiveAverages = []
			this.lastLiveIndexChecked = 0
		},

		stopRecording() {
			if (!this.isRecording) return
			// capture final duration
			this.duration = this.getDurationInSecs()
			this.isRecording = false
			// stop recording
			this.mediaRecorder.stop()
			// stop the track from recording so "recording" icon goes away
			// See: https://stackoverflow.com/questions/44274410/mediarecorder-stop-doesnt-clear-the-recording-icon-in-the-tab
			const [track] = this.stream.getAudioTracks()
			track.stop()
			track.removeEventListener('ended', this.onAudioTrackEnded)
			this.sendWorkletShutdown()
		},

		clearState() {
			// disconnect from everything
			this.streamSource?.disconnect(this.analyser)
			this.streamSource?.disconnect(this.workletNode)
			this.workletNode.port.onmessage = null
			this.mediaRecorder?.removeEventListener('dataavailable', this.onAudioData)
			this.resizeObserver?.disconnect()
			clearInterval(this.interval)
			// reset recording state
			this.startTime = null
			this.stopTime = null
			this.isRecording = false
			if (this.stream) {
				// stop MediaRecorder and stop each track individually
				const [track] = this.stream.getAudioTracks()
				track.stop()
				track.removeEventListener('ended', this.onAudioTrackEnded)
			}
			// reset all of the other state too
			this.stream = null
			this.mediaRecorder = null
			if (this.player) {
				// stop playback
				this.player.pause()
				this.player.removeEventListener('timeupdate', this.onTimeUpdate)
				this.player.removeEventListener('play', this.onPlay)
				this.player.removeEventListener('pause', this.onPause)
				this.player.removeEventListener('ended', this.onEnd)
				this.player = null
			}
			// clear up extra state
			this.analyser = null
			this.streamSource = null
			this.workletNode = null
			this.audioBlobs = []
			this.liveAverages = []
			this.drawableLiveAverages = []
			this.lastLiveIndexChecked = 0
			this.fullBlob = null
		},

		drawFullClip() {
			if (!this.fullWaveform?.length) return
			this.drawWave(this.fullWaveform, () => ({
				strokeStyle: '#B8B9BD',
			}))
		},

		drawPlayback() {
			const time = this.player.currentTime
			const total = this.fullWaveform.length
			this.drawWave(this.fullWaveform, (index) => {
				// the "played" bars need to be drawn in blue, while the "unplayed" should be gray
				const playbackPercent = Math.floor((time / this.duration) * 100) // this is how much has been played
				const barPercent = Math.floor(((index + 1) / total) * 100) // the current bar's percentage across the canvas
				const isBlue = playbackPercent > barPercent
				return { strokeStyle: isBlue ? '#49A4DA' : '#B8B9BD' }
			})
		},

		drawWave(audioData, getOptions) {
			if (this.isDrawing || !audioData?.length) {
				return
			}
			this.isDrawing = true
			requestAnimationFrame(() => {
				this.clearCanvas()
				const [WIDTH] = this.getCanvasSize()
				// In order for the animation to correctly move from right-to-left, x has to be based on
				// the number of bars we want to draw multiplied by the width of each bar and each space,
				// subtracted from the total width of the canvas
				let x = WIDTH - (BAR_WIDTH + BAR_SPACING) * audioData.length
				audioData.forEach((data, i) => {
					this.drawBar(data, x, getOptions?.(i))
					x = x + BAR_WIDTH + BAR_SPACING
				})
				this.isDrawing = false
			})
		},

		drawBar(data, x, options = {}) {
			// Data will be a 32-bit floating point number in the range [0, 1]
			const [w, HEIGHT] = this.getCanvasSize()
			// set styles for each bar
			Object.assign(this.ctx, {
				lineWidth: BAR_WIDTH,
				strokeStyle: '#49A4DA',
				lineCap: 'round',
				font: '4px',
				...options,
			})
			// To calculate barHeight, we'll multiply the data value by the height of the canvas
			// since the data value acts as a percentage of height
			let barHeight = data * HEIGHT
			// if bar is too small, give it a minimum height so at least something is drawn on the screen
			barHeight = barHeight <= MIN_BAR_HEIGHT ? MIN_BAR_HEIGHT : barHeight
			// start drawing halfway up and then go halfway down so bar is centered in the wave
			const halfHeight = barHeight / 2
			this.ctx.beginPath()
			this.ctx.moveTo(x, halfHeight)
			this.ctx.lineTo(x, -halfHeight)
			this.ctx.stroke()
		},

		getCanvasSize() {
			if (!this.$refs.canvas) return []
			const { offsetWidth, offsetHeight } = this.$refs.canvas
			return [offsetWidth, offsetHeight]
		},

		clearCanvas() {
			const [WIDTH, HEIGHT] = this.getCanvasSize()
			const halfHeight = Math.ceil(HEIGHT / 2)
			this.ctx.clearRect(0, -halfHeight, WIDTH, HEIGHT)
		},

		onCanvasResize() {
			const { offsetWidth: w, offsetHeight: h } = this.$refs.container
			if (!this.ctx) {
				this.canvas = this.$refs.canvas
				this.ctx = this.canvas.getContext('2d')
			}
			// based on updated size of the canvas, scale and translate accordingly
			const dpr = window.devicePixelRatio
			Object.assign(this.canvas, {
				width: Math.floor(w * dpr),
				height: Math.floor(h * dpr),
			})
			this.ctx.scale(dpr, dpr)
			this.ctx.translate(0, h / 2)
			// for this canvas size, calculate how many bars we can draw in the space
			this.drawableBarCount = Math.floor(w / (BAR_WIDTH + BAR_SPACING))
		},

		setUpPlayback() {
			if (this.player) return
			this.player = new Audio(URL.createObjectURL(this.fullBlob))
			this.onTimeUpdate = () => {
				this.currentPlaybackTime = this.player.currentTime
			}
			this.onPlay = () => {
				this.isPlaying = true
			}
			this.onPause = () => {
				this.isPlaying = false
			}
			this.onEnd = () => {
				this.isPlaying = false
				this.player.currentTime = 0
				this.currentPlaybackTime = 0
				this.drawPlayback()
			}
			this.player.addEventListener('timeupdate', this.onTimeUpdate)
			this.player.addEventListener('play', this.onPlay)
			this.player.addEventListener('pause', this.onPause)
			this.player.addEventListener('ended', this.onEnd)
		},

		seekPlayback(e) {
			if (this.isRecording) return
			// User clicked somewhere on the rendered waveform and wants to seek to
			// that location in the playback. We have to use the click event to calculate
			// what percentage point in the audio we should jump to
			const waveform = e.target
			const { left } = waveform.getBoundingClientRect()
			const x = e.clientX - left
			const ratio = x / waveform.offsetWidth
			const time = ratio * this.duration
			this.player.currentTime = time
			this.currentPlaybackTime = time
			this.drawPlayback()
		},

		getDurationInSecs() {
			if (!this.startTime || !this.stopTime) return 0
			return this.stopTime.diff(this.startTime, 'seconds')
		},

		onClickSend() {
			const file = new File([this.fullBlob], FILE_NAME, { type: MIME_TYPE })
			this.$emit('send', {
				file,
				duration: Math.round(this.recordTime / 1000),
			})
		},

		onClickTrash() {
			this.isClosing = true
			this.player?.pause()
			this.stopRecording()
			this.$emit('cancel')
		},

		onAudioTrackEnded() {
			this.stopRecording()
		},
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

@keyframes fadein {
	0% {
		opacity: 0;
	}
	100% {
		opacity: 1;
	}
}

.audio-recorder {
	@include flex-column-center(10px);
	justify-content: center;
	background: white;
	overflow: hidden;
	height: 62px;
	transition: height linear 150ms;
	animation: fadein ease-in-out 0.3s;
	&.at-limit {
		height: 92px;
	}
	&.closing {
		animation: fadein ease-in-out 0.3s reverse;
	}
	.pronto-icon {
		font-size: 24px;
	}
	.trash,
	.stop {
		cursor: pointer;
		flex-shrink: 0;
		color: var(--red-200);
		&:hover {
			color: var(--red-300);
		}
	}
	.play,
	.pause {
		cursor: pointer;
		color: var(--blue-300);
		flex-shrink: 0;
	}
	.send {
		cursor: pointer;
		color: var(--teal-300);
		flex-shrink: 0;
		transform: rotate(-45deg);
	}
	.wrapper {
		padding: 0 30px;
		@include flex(16px);
		width: 100%;
		.waveform {
			flex-grow: 1;
			border-radius: 6px;
			border: 1px solid var(--neutral-200);
			padding: 6px 8px;
			@include flex(8px);
			height: 42px;
			.canvas-container {
				flex-grow: 1;
				height: 30px;
				overflow: hidden;
				canvas {
					width: 100%;
					height: 30px;
					pointer-events: none;
				}
			}
		}
		.time {
			font-size: 14px;
		}
	}
}
</style>
